import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { EMPTY, Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { loadFeatureFlags } from 'src/app/features/feature-management/store/actions/feature-flags.actions';
import {
  setParticipantId,
  setSigningJoinCode,
  loadSession,
} from 'src/app/features/session/store/actions/session.actions';
import { MsalAuthActions } from '../actions/msal-auth.actions';
import { extractParticipantIdFromToken } from '../../utils/token.utils';
import { AuthenticationStatus } from '../state/msal-auth.state';

const JOIN_CODE_STORAGE_KEY = 'joinCode';
const JOIN_CODE_STORAGE_PREFIX = 'joinCode:';

function resolveJoinCode(participantId: string | null): string | null {
  const unscopedJoinCode = sessionStorage.getItem(JOIN_CODE_STORAGE_KEY);
  if (unscopedJoinCode) {
    sessionStorage.removeItem(JOIN_CODE_STORAGE_KEY);
    if (participantId) {
      sessionStorage.setItem(`${JOIN_CODE_STORAGE_PREFIX}${participantId}`, unscopedJoinCode);
    }
    return unscopedJoinCode;
  }

  if (participantId) {
    const scopedJoinCode = sessionStorage.getItem(`${JOIN_CODE_STORAGE_PREFIX}${participantId}`);
    if (scopedJoinCode) {
      return scopedJoinCode;
    }
  }

  return null;
}

@Injectable()
export class MsalAuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private whenBrowser<T>() {
    return (source: Observable<T>): Observable<T> =>
      isPlatformBrowser(this.platformId) ? source : EMPTY;
  }

  setAuthenticationState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MsalAuthActions.setAuthenticationState),
      this.whenBrowser(),
      filter(({ status }) => status === AuthenticationStatus.Authenticated),
      switchMap(({ accountInfo }) => {
        const participantId = extractParticipantIdFromToken(
          accountInfo?.idTokenClaims,
        );

        const actions: Action[] = [loadFeatureFlags()];

        if (participantId) {
          actions.push(
            setParticipantId({ participantId }),
            loadSession({ participantId }),
          );
        }

        const joinCode = resolveJoinCode(participantId);
        if (joinCode) {
          actions.push(setSigningJoinCode({ joinCode }));
        }

        return actions;
      }),
    ),
  );

  handleAuthError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MsalAuthActions.handleAuthError),
        this.whenBrowser(),
        tap(({ error }) => {
          const errorCode = error.errorCode;
          if (errorCode) {
            this.router.navigate(['/session-inaccessible']);
          }
        }),
      ),
    { dispatch: false },
  );
}
